/**
 * BuildingDetailsSidebar — Ficha de edificio en el panel lateral derecho
 *
 * @remarks
 * V1.4 — Primer hito: mueve la revisión manual de edificios del panel
 * izquierdo al panel lateral derecho, siguiendo el patrón establecido
 * por TreeDetailsPanel, WasteContainerDetailsPanel y TransitStopDetailsPanel.
 *
 * Estructura:
 * 1. Franja de datos base (área, calidad, fuente) — desde propiedades GeoJSON,
 *    sin API adicional, instantáneo.
 * 2. Formulario BuildingReviewPanel — carga revisión vía API y permite edición.
 */

import { Building2 } from 'lucide-react';
import { BuildingReviewPanel } from './BuildingReviewPanel';
import '../layouts/panels/LeftPanel/tabbed-left-panel.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatArea(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return `${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })} m²`;
}

function formatVol(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return `${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })} m³`;
}

const BUILT_AREA_METHOD_LABELS: Record<string, string> = {
  direct:          'Planta catastral',
  height_derived:  'Altura detectada',
  heuristic_1floor:'Heurística (1 planta)',
};

const LEVELS_SOURCE_LABELS: Record<string, string> = {
  cadastre:        'Catastro',
  manual:          'Manual',
  height_derived:  'Altura detectada',
  heuristic_area:  'Heurística por área',
};

const QUALITY_LABELS: Record<string, string> = {
  high:    'Alta',
  medium:  'Media',
  low:     'Baja',
  unknown: 'Desconocida',
};

const SOURCE_LABELS: Record<string, string> = {
  open_buildings:    'Open Buildings',
  open_buildings_v3: 'Open Buildings v3',
  osm:               'OpenStreetMap',
  cadastre:          'Catastro',
  manual:            'Manual',
};

function qualityClass(q: string): string {
  if (q === 'high')   return 'bds-badge bds-badge--high';
  if (q === 'medium') return 'bds-badge bds-badge--medium';
  if (q === 'low')    return 'bds-badge bds-badge--low';
  return 'bds-badge bds-badge--unknown';
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  buildingId:        string;
  projectId:         string;
  initialProperties: Record<string, unknown>;
  onClose:           () => void;
  onSaved:           () => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function BuildingDetailsSidebar({
  buildingId,
  projectId,
  initialProperties,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const area             = initialProperties['footprintAreaM2'];
  const quality          = initialProperties['qualityStatus']       as string | undefined;
  const source           = initialProperties['sourceType']          as string | undefined;
  const levelsEstimated  = initialProperties['levelsEstimated']     as number | null | undefined;
  const levelsSource     = initialProperties['levelsSource']        as string | null | undefined;
  const builtAreaEst     = initialProperties['builtAreaEstimatedM2']as number | null | undefined;
  const builtAreaMethod  = initialProperties['builtAreaMethod']     as string | null | undefined;

  // Volumen estimado: builtAreaEstimated × 3 m altura media por nivel
  const HEIGHT_PER_LEVEL = 3;
  const volEstM3 =
    builtAreaEst != null && Number(builtAreaEst) > 0
      ? Number(builtAreaEst) * HEIGHT_PER_LEVEL
      : null;

  const methodLabel =
    builtAreaMethod != null
      ? (BUILT_AREA_METHOD_LABELS[builtAreaMethod] ?? builtAreaMethod)
      : levelsEstimated != null
        ? (LEVELS_SOURCE_LABELS[levelsSource ?? ''] ?? levelsSource ?? 'Estimada')
        : null;

  return (
    <div className="bds-wrapper">

      {/* ── Franja de datos base ── */}
      <div className="bds-info-strip">
        <Building2 size={13} className="bds-info-icon" />

        <span className="bds-info-item">
          <span className="bds-info-key">Área</span>
          <span className="bds-info-val">{formatArea(area)}</span>
        </span>

        {quality != null && (
          <span className="bds-info-item">
            <span className={qualityClass(quality)}>
              {QUALITY_LABELS[quality] ?? quality}
            </span>
          </span>
        )}

        {source != null && (
          <span className="bds-info-item">
            <span className="bds-info-source">
              {SOURCE_LABELS[source] ?? source}
            </span>
          </span>
        )}
      </div>

      {/* ── Dimensión edilicia ── */}
      <div className="bds-dim-section">
        <span className="bds-dim-title">Dimensión edilicia</span>
        <div className="bds-dim-grid">
          <span className="bds-dim-key">Huella</span>
          <span className="bds-dim-val">{formatArea(area)}</span>

          <span className="bds-dim-key">Niveles est.</span>
          <span className="bds-dim-val">
            {levelsEstimated != null ? levelsEstimated : <span className="bds-dim-none">sin dato</span>}
          </span>

          <span className="bds-dim-key">Masa const. est.</span>
          <span className="bds-dim-val">
            {builtAreaEst != null && Number(builtAreaEst) > 0
              ? formatArea(builtAreaEst)
              : <span className="bds-dim-none">no estimada</span>}
          </span>

          <span className="bds-dim-key">Volumen est.</span>
          <span className="bds-dim-val">
            {volEstM3 != null
              ? formatVol(volEstM3)
              : <span className="bds-dim-none">no estimado</span>}
          </span>

          {methodLabel != null && (
            <>
              <span className="bds-dim-key">Fuente</span>
              <span className="bds-dim-val bds-dim-val--source">{methodLabel}</span>
            </>
          )}
          {volEstM3 != null && (
            <>
              <span className="bds-dim-key">Altura/nivel</span>
              <span className="bds-dim-val bds-dim-val--source">{HEIGHT_PER_LEVEL} m (supuesto)</span>
            </>
          )}
        </div>
      </div>

      {/* ── Formulario de revisión ── */}
      <BuildingReviewPanel
        buildingId={buildingId}
        projectId={projectId}
        initialProperties={initialProperties}
        onClose={onClose}
        onSaved={onSaved}
      />

    </div>
  );
}
